import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styles from './FloatingPanel.module.css';

export type Corner = 'bottom-right' | 'bottom-left' | 'top-left' | 'top-right';
export const CORNER_ORDER: Corner[] = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
export interface Pos { x: number; y: number }
export const MARGIN = 16;
export const KEY_STEP = 24;
const DRAG_THRESHOLD = 4;

export function clampPosition(p: Pos, panel: { w: number; h: number }, box: { w: number; h: number }): Pos {
  return {
    x: Math.min(Math.max(p.x, MARGIN), Math.max(MARGIN, box.w - panel.w - MARGIN)),
    y: Math.min(Math.max(p.y, MARGIN), Math.max(MARGIN, box.h - panel.h - MARGIN)),
  };
}

export function cornerPosition(c: Corner, panel: { w: number; h: number }, box: { w: number; h: number }): Pos {
  const pos: Pos =
    c === 'bottom-right' ? { x: box.w - panel.w - MARGIN, y: box.h - panel.h - MARGIN }
      : c === 'bottom-left' ? { x: MARGIN, y: box.h - panel.h - MARGIN }
        : c === 'top-left' ? { x: MARGIN, y: MARGIN }
          : { x: box.w - panel.w - MARGIN, y: MARGIN };
  return clampPosition(pos, panel, box);
}

export function nextCorner(c: Corner): Corner {
  return CORNER_ORDER[(CORNER_ORDER.indexOf(c) + 1) % CORNER_ORDER.length];
}

interface FloatingPanelProps {
  children: React.ReactNode;
  storageKey: string;
  gripLabel?: string;
  className?: string;
  /** Couleur CSS de l'ombre (ex. couleur de l'état vocal) ; sans elle, ombre neutre. */
  glow?: string;
}

export function FloatingPanel({ children, storageKey, gripLabel = 'Déplacer la fenêtre', className, glow }: FloatingPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos>({ x: MARGIN, y: MARGIN });
  const [corner, setCorner] = useState<Corner>('bottom-right');
  const [dragging, setDragging] = useState(false);
  const draggedRef = useRef(false);
  const dragStart = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return { panel: { w: 0, h: 0 }, box: { w: window.innerWidth, h: window.innerHeight } };
    const parent = el.offsetParent as HTMLElement | null;
    return {
      panel: { w: el.offsetWidth, h: el.offsetHeight },
      box: parent ? { w: parent.clientWidth, h: parent.clientHeight } : { w: window.innerWidth, h: window.innerHeight },
    };
  }, []);

  const save = useCallback((p: Pos) => {
    try { localStorage.setItem(storageKey, JSON.stringify(p)); } catch { /* ignoré */ }
  }, [storageKey]);

  useLayoutEffect(() => {
    const { panel, box } = measure();
    let p: Pos | null = null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Pos;
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') p = parsed;
      }
    } catch { /* ignoré */ }
    setPos(clampPosition(p ?? cornerPosition('bottom-right', panel, box), panel, box));
  }, [measure, storageKey]);

  useLayoutEffect(() => {
    const onResize = () => {
      const { panel, box } = measure();
      setPos((prev) => clampPosition(prev, panel, box));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  // Le contenu change de hauteur (panneau de contexte) : re-borner dans le conteneur.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const { panel, box } = measure();
      setPos((prev) => clampPosition(prev, panel, box));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Glisser depuis tout le fond de la fenêtre, sauf depuis ses commandes (cercle, chevron, saisie) ; la poignée reste un point de prise.
    const t = e.target as HTMLElement;
    if (t.closest('input, textarea, select, a, label') || (t.closest('button') && !t.closest('[data-grip]'))) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y };
    draggedRef.current = false;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    if (!start) return;
    const dx = e.clientX - start.px;
    const dy = e.clientY - start.py;
    if (!draggedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    draggedRef.current = true;
    setDragging(true);
    const { panel, box } = measure();
    setPos(clampPosition({ x: start.x + dx, y: start.y + dy }, panel, box));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    dragStart.current = null;
    setDragging(false);
    if (draggedRef.current) save(pos);
  };

  const onClick = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    const next = nextCorner(corner);
    setCorner(next);
    const { panel, box } = measure();
    const p = cornerPosition(next, panel, box);
    setPos(p);
    save(p);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!e.shiftKey) return;
    const map: Record<string, Pos> = {
      ArrowLeft: { x: -KEY_STEP, y: 0 }, ArrowRight: { x: KEY_STEP, y: 0 },
      ArrowUp: { x: 0, y: -KEY_STEP }, ArrowDown: { x: 0, y: KEY_STEP },
    };
    const d = map[e.key];
    if (!d) return;
    e.preventDefault();
    const { panel, box } = measure();
    const p = clampPosition({ x: pos.x + d.x, y: pos.y + d.y }, panel, box);
    setPos(p);
    save(p);
  };

  return (
    <div
      ref={ref}
      className={styles.panel + (dragging ? ` ${styles.dragging}` : '') + (glow ? ` ${styles.glow}` : '') + (className ? ` ${className}` : '')}
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, ...(glow ? { '--panel-glow': glow } : {}) } as React.CSSProperties}
      data-corner={corner}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <button
        type="button"
        className={styles.grip}
        aria-label={gripLabel}
        title="Glisser la fenêtre pour la déplacer · toucher la poignée : coin suivant"
        data-grip
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <span className={styles.gripBar} aria-hidden="true" />
      </button>
      <div className={styles.body}>{children}</div>
    </div>
  );
}
